import '.ai{position:relative;}.ssr-plugin'
import '.a0{height:44px;}.ssr-plugin'
import '.b1{box-sizing:border-box;}.ssr-plugin'
import '.cy{justify-content:center;}.ssr-plugin'
import '.w{align-items:center;}.ssr-plugin'
import '.b9{inset:0px;}.ssr-plugin'
import '.e{display:flex;}.ssr-plugin'
import '.e5{border:none;}.ssr-plugin'
import '.g8{background:transparent;}.ssr-plugin'
import '.gu{color:inherit;}.ssr-plugin'
import '.ce{font-size:16px;}.ssr-plugin'
import '.gv{transition:opacity 200ms ease;}.ssr-plugin'
import '.a6{position:absolute;}.ssr-plugin'
import '.ao{justify-content:space-between;}.ssr-plugin'
import '.gw{padding:0px 4px;}.ssr-plugin'
import '.gx{display:inline-flex;}.ssr-plugin'
import '.gy{transition:opacity 250ms ease, transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1);}.ssr-plugin'
import '.c2{overflow:hidden;}.ssr-plugin'
import '.gz{min-width:1.2em;}.ssr-plugin'
import '.h0{height:1.2em;}.ssr-plugin'
import '.h1{line-height:1.2;}.ssr-plugin'
import { styled, Style } from 'inlines'
import { useRef, useState } from 'react'
import { useLocation } from 'wouter'
import { colors } from '../utils/colors.js'
import { IconButton } from './IconButton.js'
import { Icon } from '../assets/icons/index.js'
import { useTrack } from '../hooks/useTrack.js'
import { AnalyticsEvent } from '../../../shared/analyticsEvents.js'
import { transparentColor } from '../utils/transparentcolor.js'
import { useDictionary } from '../hooks/useDictionary.js'
import { useVotingRegion } from '../hooks/useVotingRegion.js'

type VoteButtonProps = {
  votingRegion?: string
  votes?: Record<string, number>
  setVotes?: (selected: Record<string, number>) => void
  maxed?: boolean
  id?: string
  style?: Style
}

// esbuild constant-folds `${id}-add` in JSX into a string literal from the
// Card call site, breaking the modal's add/remove button IDs.
// A separate function prevents the optimizer from inlining across call sites.
const deriveId = (base: string | undefined, suffix: string) =>
  base ? base + '-' + suffix : undefined

export const VoteButton = ({
  votes,
  setVotes,
  votingRegion,
  maxed,
  id,
  style,
}: VoteButtonProps) => {
  const track = useTrack('voting')

  const removeId = deriveId(id, 'remove')
  const addId = deriveId(id, 'add')

  if (!votes || !setVotes || !votingRegion) return null

  const dictionary = useDictionary()
  const [data] = useVotingRegion()
  const [, setLocation] = useLocation()
  const isOnlineVoting = data.voteType === 'online'

  const hasVotes = isOnlineVoting && !!votes[votingRegion]
  const canHover = !hasVotes && !maxed
  const transition =
    'background-color 200ms ease, color 200ms ease, border-color 200ms ease, border-radius 200ms ease, opacity 200ms ease, width 200ms ease, scale 200ms ease'

  return (
    <styled.div className="ai a0 b1 cy w"
      style={{
        width: hasVotes ? 94 : 'fit-content',
        border: `1px solid ${hasVotes ? 'transparent' : colors.border}`,
        borderRadius: hasVotes ? 50 : 24,
        backgroundColor: hasVotes ? colors.white : colors.border,
        color: hasVotes ? colors.secondary : 'inherit',
        opacity: !hasVotes && maxed ? 0.25 : 1,
        transition,
        '&:hover': {
          scale: canHover ? 0.98 : 1,
          backgroundColor: canHover
            ? transparentColor(colors.white, 0.2)
            : undefined,
        },
        display: data?.code === votingRegion ? 'none' : 'flex',
        ...style,
      }}
    >
      <button className="ai b9 e w cy e5 g8 b1 gu ce gv"
        aria-label={'vote-button'}
        id={id}
        onClick={
          maxed
            ? undefined
            : () => {
                track(AnalyticsEvent.VOTING_CLICK, 'add_vote')
                if (!isOnlineVoting) {
                  setVotes({ [votingRegion]: 1 })
                  setLocation('/cart')
                  return
                }
                votes[votingRegion] ??= 0
                votes[votingRegion]++
                setVotes({ ...votes })
              }
        }
        style={{
          padding: hasVotes ? 0 : '0px 16px',
          cursor: maxed ? 'not-allowed' : 'pointer',
          opacity: hasVotes ? 0 : 1,
          pointerEvents: hasVotes ? 'none' : 'auto',}}
      >
        {dictionary.voteButton}
      </button>
      <styled.div className="a6 b9 e w ao gw"
        style={{
          pointerEvents: hasVotes ? 'auto' : 'none',
          '& svg': {
            minWidth: '24px !important',
          },
        }}
      >
        <span className="gx gy"
          style={{
            opacity: hasVotes ? 1 : 0,
            transform: hasVotes ? 'scale(1)' : 'scale(0.3)',}}
        >
          <IconButton
            id={removeId}
            ariaLabel={'remove-vote'}
            icon={<Icon color={colors.secondary} type="removeIcon" />}
            onClick={(e) => {
              e.stopPropagation()
              votes[votingRegion]--
              if (votes[votingRegion] <= 0) {
                delete votes[votingRegion]
              }
              track(AnalyticsEvent.VOTING_CLICK, 'remove_vote')
              setVotes({ ...votes })
            }}
            style={{ width: 30 }}
          />
        </span>
        <span className="gy"
          style={{
            opacity: hasVotes ? 1 : 0,
            transform: hasVotes ? 'scale(1)' : 'scale(0.6)',}}
        >
          <AnimatedCount value={votes[votingRegion] ?? 0} />
        </span>
        <span className="gx gy"
          style={{
            opacity: hasVotes ? 1 : 0,
            transform: hasVotes ? 'scale(1)' : 'scale(0.3)',}}
        >
          <IconButton
            id={addId}
            ariaLabel={'add-vote'}
            icon={
              <Icon
                color={colors.secondary}
                type="addCircleIcon"
                style={{
                  opacity: maxed ? 0.25 : 1,
                  cursor: maxed ? 'not-allowed' : 'pointer',
                }}
              />
            }
            onClick={
              maxed
                ? undefined
                : () => {
                    track(AnalyticsEvent.VOTING_CLICK, 'add_vote')
                    votes[votingRegion] ??= 0
                    votes[votingRegion]++
                    setVotes({ ...votes })
                  }
            }
            style={{ width: 30 }}
          />
        </span>
      </styled.div>
    </styled.div>
  )
}

type AnimatedCountProps = {
  value: number
}

const AnimatedCount = ({ value }: AnimatedCountProps) => {
  const prevRef = useRef({
    value,
    exiting: null as number | null,
    direction: 0 as 1 | -1 | 0,
    key: 0,
  })

  const direction =
    value > prevRef.current.value ? 1 : value < prevRef.current.value ? -1 : 0
  const key = direction !== 0 ? prevRef.current.key + 1 : prevRef.current.key
  const stableDirection = direction !== 0 ? direction : prevRef.current.direction

  const state = {
    current: value,
    exiting: direction !== 0 ? prevRef.current.value : null,
    direction: stableDirection,
    key,
  }

  prevRef.current = {
    value,
    exiting: state.exiting,
    direction: stableDirection,
    key,
  }

  return (
    <span className="ai gx w cy c2 gz h0 h1"
    >
      <style>{`
        @keyframes vote-count-in-down {
          from { transform: translateY(-100%); opacity: 0; }
          to   { transform: translateY(0%);    opacity: 1; }
        }
        @keyframes vote-count-in-up {
          from { transform: translateY(100%);  opacity: 0; }
          to   { transform: translateY(0%);    opacity: 1; }
        }
        @keyframes vote-count-out-down {
          from { transform: translateY(0%);    opacity: 1; }
          to   { transform: translateY(100%);  opacity: 0; }
        }
        @keyframes vote-count-out-up {
          from { transform: translateY(0%);    opacity: 1; }
          to   { transform: translateY(-100%); opacity: 0; }
        }
      `}</style>
      {state.exiting !== null && (
        <span className="a6"
          key={`exit-${state.key}`}
          onAnimationEnd={() => {
            prevRef.current.exiting = null
          }}
          style={{
            animation: `${
              state.direction === 1
                ? 'vote-count-out-down'
                : 'vote-count-out-up'
            } 0.3s cubic-bezier(0.22, 1, 0.36, 1) forwards`,
          }}
        >
          {state.exiting}
        </span>
      )}
      <span
        key={`enter-${state.key}`}
        style={{
          animation:
            state.key > 0
              ? `${
                  state.direction === 1
                    ? 'vote-count-in-down'
                    : 'vote-count-in-up'
                } 0.3s cubic-bezier(0.22, 1, 0.36, 1) forwards`
              : 'none',
        }}
      >
        {state.current}
      </span>
    </span>
  )
}
